import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  Loader2,
  Pause,
  Play,
  DollarSign,
  Activity,
  Workflow,
  BarChart3,
  ListChecks,
  Clock,
} from 'lucide-react';
import { useFleetStore } from '@/stores/fleet';
import { knowledgeFleetApi, queueControlApi, type QueueTask } from '@/services/api';
import { ScoringWeightEditor } from '@/components/knowledge/fleet/ScoringWeightEditor';

function timeAgo(isoStr?: string): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

const TASK_STATUSES = ['all', 'ready', 'executing', 'completed', 'deferred', 'saturated', 'failed'];

type Tab = 'tasks' | 'depth' | 'lineage';

export function ClientFleetDetailPage() {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const {
    fleetConfig,
    budget,
    recentRoots,
    fleetConfigLoading,
    fetchFleetConfig,
    fetchRecentRoots,
    updateFleetConfig,
  } = useFleetStore();

  const [tasks, setTasks] = useState<QueueTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tab, setTab] = useState<Tab>('tasks');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchFleetConfig(clientId);
      fetchRecentRoots(clientId);
    }
  }, [clientId, fetchFleetConfig, fetchRecentRoots]);

  useEffect(() => {
    if (!clientId) return;
    setTasksLoading(true);
    const filter = statusFilter === 'all' ? undefined : statusFilter;
    queueControlApi
      .listTasksForClient(clientId, filter, 50)
      .then(setTasks)
      .catch((e) => {
        console.warn('Failed to load tasks:', e);
        setTasks([]);
      })
      .finally(() => setTasksLoading(false));
  }, [clientId, statusFilter]);

  const completedTasks = useMemo(() => tasks.filter((t) => t.status === 'completed'), [tasks]);
  const activeTaskCount = useMemo(
    () => tasks.filter((t) => t.status === 'executing' || t.status === 'dispatched').length,
    [tasks],
  );

  const todaySpendEstimate = useMemo(() => {
    // Coarse proxy: clip today's tasks' priority * est_cost if available. The
    // backend's authoritative daily-spend value lives on the budget envelope;
    // we show both when available.
    return budget?.global_utilization != null && fleetConfig?.daily_budget_usd != null
      ? budget.global_utilization * fleetConfig.daily_budget_usd
      : null;
  }, [budget, fleetConfig]);

  if (!clientId) {
    return (
      <div className="p-6 text-sm text-slate-400">No client ID specified.</div>
    );
  }

  const dailyBudget = fleetConfig?.daily_budget_usd;
  const spendPct =
    dailyBudget && todaySpendEstimate
      ? Math.min(100, (todaySpendEstimate / dailyBudget) * 100)
      : null;

  const togglePaused = async () => {
    if (!fleetConfig) return;
    setSaving(true);
    try {
      await updateFleetConfig({
        client_id: clientId,
        paused: !fleetConfig.paused,
        max_agents_per_cycle: fleetConfig.max_agents_per_cycle,
        daily_budget_usd: fleetConfig.daily_budget_usd,
        min_task_priority: fleetConfig.min_task_priority,
        max_probe_batches: fleetConfig.max_probe_batches,
        max_research_depth: fleetConfig.max_research_depth,
        scoring_weight_overrides: fleetConfig.scoring_weight_overrides ?? {},
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/knowledge/fleet"
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} />
            Fleet
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-navy-900">{clientId}</h1>
              {fleetConfig?.paused ? (
                <span className="text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                  Paused
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Per-client fleet health, task history, and lineage
            </p>
          </div>
        </div>
        <button
          onClick={togglePaused}
          disabled={saving || fleetConfigLoading || !fleetConfig}
          className={clsx(
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border',
            fleetConfig?.paused
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
              : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
            'disabled:opacity-40',
          )}
        >
          {saving ? (
            <Loader2 size={11} className="animate-spin" />
          ) : fleetConfig?.paused ? (
            <Play size={11} />
          ) : (
            <Pause size={11} />
          )}
          {fleetConfig?.paused ? 'Resume' : 'Pause'}
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatTile
          icon={DollarSign}
          label="Budget"
          value={dailyBudget != null ? `$${dailyBudget.toFixed(2)}` : '—'}
          caption={spendPct != null ? `${spendPct.toFixed(0)}% used today` : 'per day'}
          accent={spendPct != null && spendPct > 80 ? 'rose' : 'teal'}
        />
        <StatTile
          icon={Activity}
          label="Active Tasks"
          value={String(activeTaskCount)}
          caption="executing/dispatched"
        />
        <StatTile
          icon={ListChecks}
          label="Recent Tasks"
          value={String(tasks.length)}
          caption={statusFilter === 'all' ? 'all statuses' : statusFilter}
        />
        <StatTile
          icon={Workflow}
          label="Lineage Roots"
          value={String(recentRoots.length)}
          caption="recent chains"
        />
      </div>

      {/* Budget progress */}
      {spendPct != null && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-700">Today's spend</span>
            <span className="text-xs text-slate-500">
              {todaySpendEstimate?.toFixed(2)} / {dailyBudget?.toFixed(2)} USD
            </span>
          </div>
          <div className="bg-slate-100 rounded-full overflow-hidden h-2.5">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-500',
                spendPct > 90
                  ? 'bg-rose-400'
                  : spendPct > 70
                  ? 'bg-amber-400'
                  : 'bg-brand-teal',
              )}
              style={{ width: `${Math.min(spendPct, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Scoring Weight Editor — primary client-scoped config surface */}
      <ScoringWeightEditor clientId={clientId} />

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <TabButton active={tab === 'tasks'} onClick={() => setTab('tasks')}>
          <ListChecks size={14} /> Task History
        </TabButton>
        <TabButton active={tab === 'depth'} onClick={() => setTab('depth')}>
          <BarChart3 size={14} /> Depth Distribution
        </TabButton>
        <TabButton active={tab === 'lineage'} onClick={() => setTab('lineage')}>
          <Workflow size={14} /> Lineage Summary
        </TabButton>
      </div>

      {tab === 'tasks' && (
        <TaskHistoryPanel
          tasks={tasks}
          loading={tasksLoading}
          statusFilter={statusFilter}
          onFilterChange={setStatusFilter}
        />
      )}
      {tab === 'depth' && <DepthDistributionPanel tasks={completedTasks} clientId={clientId} />}
      {tab === 'lineage' && <LineageSummaryPanel clientId={clientId} />}
    </div>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  caption,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  caption?: string;
  accent?: 'teal' | 'rose';
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon
          size={12}
          className={clsx(
            accent === 'rose' ? 'text-rose-500' : accent === 'teal' ? 'text-brand-teal' : 'text-slate-400',
          )}
        />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-xl font-bold text-navy-900">{value}</div>
      {caption && <div className="text-[10px] text-slate-400 mt-0.5">{caption}</div>}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors',
        active
          ? 'text-brand-teal border-b-2 border-brand-teal'
          : 'text-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}

function TaskHistoryPanel({
  tasks,
  loading,
  statusFilter,
  onFilterChange,
}: {
  tasks: QueueTask[];
  loading: boolean;
  statusFilter: string;
  onFilterChange: (s: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {TASK_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => onFilterChange(s)}
            className={clsx(
              'px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all border',
              statusFilter === s
                ? 'bg-brand-teal/10 border-brand-teal/30 text-brand-teal-dark'
                : 'border-slate-200 text-slate-500 hover:border-slate-300',
            )}
          >
            {s}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="py-8 text-sm text-slate-400 text-center">
          <Loader2 size={14} className="animate-spin inline mr-2" />
          Loading tasks...
        </div>
      ) : tasks.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">
          No tasks for this client/filter.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase">Type</th>
                <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase">Status</th>
                <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase">Priority</th>
                <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase">Yield</th>
                <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tasks.map((t) => (
                <tr key={t.task_id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-700">{t.task_type ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
                      {t.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-700">{t.priority}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {t.yield_estimate != null ? (t.yield_estimate * 100).toFixed(0) + '%' : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-400">
                    <Clock size={10} className="inline mr-1" />
                    {timeAgo(t.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DepthDistributionPanel({
  tasks,
  clientId,
}: {
  tasks: QueueTask[];
  clientId: string;
}) {
  // The QueueTask model doesn't carry depth directly; derive it from lineage
  // roots for this client instead. We also compute a lightweight histogram of
  // completed-tasks-per-status as a secondary view.
  const { recentRoots } = useFleetStore();

  // Fetch chains for each root in parallel to tally depth counts.
  const [depthCounts, setDepthCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId || recentRoots.length === 0) {
      setDepthCounts({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      recentRoots.slice(0, 20).map((r) => knowledgeFleetApi.getLineageChain(r.id)),
    )
      .then((chains) => {
        if (cancelled) return;
        const counts: Record<number, number> = {};
        for (const chain of chains) {
          for (const node of chain.nodes) {
            if (node.status === 'completed') {
              counts[node.depth] = (counts[node.depth] ?? 0) + 1;
            }
          }
        }
        setDepthCounts(counts);
      })
      .catch(() => {
        if (!cancelled) setDepthCounts({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, recentRoots]);

  const entries = Object.entries(depthCounts)
    .map(([k, v]) => ({ depth: Number(k), count: v }))
    .sort((a, b) => a.depth - b.depth);
  const max = entries.length > 0 ? Math.max(...entries.map((e) => e.count)) : 1;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-navy-900 text-sm">Completed Task Depth</h2>
        <span className="text-[10px] text-slate-400">
          sampled from up to 20 recent chains
        </span>
      </div>
      {loading ? (
        <div className="py-8 text-sm text-slate-400 text-center">
          <Loader2 size={14} className="animate-spin inline mr-2" />
          Computing distribution...
        </div>
      ) : entries.length === 0 ? (
        <div className="py-8 text-sm text-slate-400 text-center">
          No completed tasks in recent chains.
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(({ depth, count }) => (
            <div key={depth} className="flex items-center gap-3">
              <span className="text-xs font-mono text-slate-500 w-14">depth {depth}</span>
              <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-full rounded-full bg-brand-teal transition-all duration-500"
                  style={{ width: `${(count / max) * 100}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-navy-900 w-8 text-right">{count}</span>
            </div>
          ))}
          <div className="text-[10px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
            {tasks.length} queue-side tasks loaded separately in the Task History tab.
          </div>
        </div>
      )}
    </div>
  );
}

function LineageSummaryPanel({ clientId }: { clientId: string }) {
  const { recentRoots, fetchRecentRoots } = useFleetStore();

  useEffect(() => {
    fetchRecentRoots(clientId);
  }, [clientId, fetchRecentRoots]);

  if (recentRoots.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center text-sm text-slate-400">
        No lineage roots recorded for this client.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
      {recentRoots.map((r) => (
        <Link
          key={r.id}
          to={`/knowledge/estate/lineage/${r.id}`}
          className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
        >
          {r.research_type && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {r.research_type}
            </span>
          )}
          <span className="text-sm text-slate-700 truncate flex-1">
            {r.label || r.target_entity_name || r.target_entity_type || r.id.slice(0, 8)}
          </span>
          <span className="text-xs text-slate-400">{timeAgo(r.created_at ?? undefined)}</span>
        </Link>
      ))}
    </div>
  );
}
